import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getMetricsSummary,
  getMetricsAnalytics,
  getMetricsRecords,
  getMetricsDetail,
  getPharmacyComparison,
} from '@/features/internal/metrics/apis/metrics'
import {
  MetricsSummaryRequest,
  MetricsSummaryResponse,
  MetricsAnalyticsRequest,
  MetricsAnalyticsResponse,
  MetricsRecordsRequest,
  MetricsRecordsResponse,
  MetricsDetail,
  PharmacyComparisonParams,
  PharmacyComparisonResponse,
} from '@/features/internal/metrics/types/metrics'

// ========================================
// useMetricsSummaryQuery
// ========================================

interface UseMetricsSummaryQueryOptions {
  params: MetricsSummaryRequest
  enabled?: boolean
}

interface UseMetricsSummaryQueryReturn {
  data: MetricsSummaryResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * メトリクスサマリー取得クエリ（TanStack Query）
 *
 * 責務:
 * - 基本KPIデータの取得
 * - キャッシュ管理（5分）
 *
 * 使用例:
 * ```tsx
 * const { data, isLoading, error } = useMetricsSummaryQuery({
 *   params: {
 *     date_range: { start: '2025-01-01', end: '2025-01-31' },
 *     include_previous_period: true,
 *   },
 * })
 * ```
 */
export const useMetricsSummaryQuery = (
  options: UseMetricsSummaryQueryOptions
): UseMetricsSummaryQueryReturn => {
  const { params, enabled = true } = options

  const query = useQuery({
    // params 全体を queryKey に含める（@tanstack/query/exhaustive-deps 対応）
    queryKey: queryKeys.metrics.summary(params),
    queryFn: async (): Promise<MetricsSummaryResponse> => {
      const data = await getMetricsSummary(params)
      return data
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: () => query.refetch(),
  }
}

// ========================================
// useMetricsAnalyticsQuery
// ========================================

interface UseMetricsAnalyticsQueryOptions {
  params: MetricsAnalyticsRequest
  enabled?: boolean
}

interface UseMetricsAnalyticsQueryReturn {
  data: MetricsAnalyticsResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * メトリクス分析データ取得クエリ（TanStack Query）
 *
 * 責務:
 * - トレンド・分布データの取得
 * - キャッシュ管理（5分）
 *
 * 使用例:
 * ```tsx
 * const { data, isLoading, error } = useMetricsAnalyticsQuery({
 *   params: {
 *     date_range: { start: '2025-01-01', end: '2025-01-31' },
 *     period: 'daily',
 *   },
 * })
 * ```
 */
export const useMetricsAnalyticsQuery = (
  options: UseMetricsAnalyticsQueryOptions
): UseMetricsAnalyticsQueryReturn => {
  const { params, enabled = true } = options

  const query = useQuery({
    // params 全体を queryKey に含める（@tanstack/query/exhaustive-deps 対応）
    queryKey: queryKeys.metrics.analytics(params),
    queryFn: async (): Promise<MetricsAnalyticsResponse> => {
      const data = await getMetricsAnalytics(params)
      return data
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: () => query.refetch(),
  }
}

// ========================================
// useMetricsRecordsQuery
// ========================================

interface UseMetricsRecordsQueryOptions {
  params: MetricsRecordsRequest
  enabled?: boolean
}

interface UseMetricsRecordsQueryReturn {
  data: MetricsRecordsResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * メトリクスレコード一覧取得クエリ（TanStack Query）
 *
 * 責務:
 * - 指導実績レコード一覧の取得
 * - ページネーション・フィルタ・ソート対応
 * - キャッシュ管理（5分）
 *
 * 使用例:
 * ```tsx
 * const { data, isLoading, error } = useMetricsRecordsQuery({
 *   params: {
 *     page: 1,
 *     page_size: 20,
 *     date_range: { start: '2025-01-01', end: '2025-01-31' },
 *   },
 * })
 * ```
 */
export const useMetricsRecordsQuery = (
  options: UseMetricsRecordsQueryOptions
): UseMetricsRecordsQueryReturn => {
  const { params, enabled = true } = options

  const query = useQuery({
    // パラメータ全体を queryKey に含めてフィルタ・ソート・ページネーション変更時にリフェッチ
    queryKey: queryKeys.metrics.records(params),
    queryFn: async (): Promise<MetricsRecordsResponse> => {
      const data = await getMetricsRecords(params)
      return data
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: () => query.refetch(),
  }
}

// ========================================
// useMetricsDetailQuery
// ========================================

interface UseMetricsDetailQueryOptions {
  id: string
  enabled?: boolean
}

interface UseMetricsDetailQueryReturn {
  data: MetricsDetail | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * メトリクス詳細取得クエリ（TanStack Query）
 *
 * 責務:
 * - 指導実績の詳細データ取得
 * - キャッシュ管理（5分）
 *
 * 使用例:
 * ```tsx
 * const { data, isLoading, error } = useMetricsDetailQuery({
 *   id: 'metrics-id-123',
 * })
 * ```
 */
export const useMetricsDetailQuery = (
  options: UseMetricsDetailQueryOptions
): UseMetricsDetailQueryReturn => {
  const { id, enabled = true } = options

  const query = useQuery({
    queryKey: queryKeys.metrics.detail(id),
    queryFn: async (): Promise<MetricsDetail> => {
      const data = await getMetricsDetail(id)
      return data
    },
    enabled: enabled && !!id,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: () => query.refetch(),
  }
}

// ========================================
// usePharmacyComparisonQuery
// ========================================

interface UsePharmacyComparisonQueryOptions {
  params: PharmacyComparisonParams
  enabled?: boolean
}

interface UsePharmacyComparisonQueryReturn {
  data: PharmacyComparisonResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * 薬局別比較データ取得クエリ（TanStack Query）
 *
 * 責務:
 * - 薬局別の比較データ取得
 * - ページネーション・ソート対応
 * - キャッシュ管理（5分）
 *
 * 使用例:
 * ```tsx
 * const { data, isLoading, error } = usePharmacyComparisonQuery({
 *   params: {
 *     start_date: '2025-01-01',
 *     end_date: '2025-01-31',
 *     sort_by: 'average_duration',
 *     sort_order: 'desc',
 *   },
 * })
 * ```
 */
export const usePharmacyComparisonQuery = (
  options: UsePharmacyComparisonQueryOptions
): UsePharmacyComparisonQueryReturn => {
  const { params, enabled = true } = options

  const query = useQuery({
    // params 全体を queryKey に含める（@tanstack/query/exhaustive-deps 対応）
    queryKey: queryKeys.metrics.comparison(params),
    queryFn: async (): Promise<PharmacyComparisonResponse> => {
      const data = await getPharmacyComparison(params)
      return data
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: () => query.refetch(),
  }
}
